import { action, computed, observable } from 'mobx'
import type {
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetWorkspaceSelectionFn,
} from '@/features/iam'
import type { LastWorkspaceStorage } from '@/features/iam/workspaces/LastWorkspaceStorage'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { ColumnLayout } from '@/infra/ColumnLayout'
import { type Storage } from '@/infra/storage'
import { Id } from '@/infra/id'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { links } from '@/utils/link'
import { type UserSearchViewModel } from '@/features/user-management/UserSearchViewModel'
import { WorkspaceSelectorViewModel } from '@/features/user-management/WorkspaceSelectorViewModel'
import { type EditUserRoleDialogViewModel } from '@/features/user-management/EditUserRoleDialog/EditUserRoleDialogViewModel'
import { type ManageSkillsDialogViewModel } from '@/features/user-management/ManageSkillsDialog/ManageSkillsDialogViewModel'
import { type ManageUserCapacityDialogViewModel } from '@/features/user-management/ManageUserCapacityDialog/ManageUserCapacityDialogViewModel'
import { type RemoveFromWorkspaceDialogViewModel } from '@/features/user-management/RemoveFromWorkspaceDialog/RemoveFromWorkspaceDialogViewModel'
import { type RestoreToWorkspaceDialogViewModel } from '@/features/user-management/RestoreToWorkspaceDialog/RestoreToWorkspaceDialogViewModel'
import { type WorkspaceInvitationDialogViewModel } from '@/features/user-management/WorkspaceInvitationDialog/WorkspaceInvitationDialogViewModel'

export interface UserColumn {
  id: string
  header: string
  modifyVisibility: boolean
  sortable: boolean
  visible: boolean
}

const userManagementColumns: UserColumn[] = [
  {
    id: 'name',
    header: 'Name',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'email',
    header: 'Email',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'userType',
    header: 'User Type',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'lastLogin',
    header: 'Last Login',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'dateCreated',
    header: 'Date Created',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
]

type CreateWorkspaceInvitationDialogViewModelFn = (vmOptions: {
  selectedWorkspace: () => Workspace
  selectedUserIds: () => Set<Id>
}) => WorkspaceInvitationDialogViewModel

type CreateManageSkillsDialogViewModelFn = (vmOptions: {
  selectedWorkspace: () => Workspace
  selectedUserIds: () => Set<Id>
  users: () => readonly SearchableUser[]
  toggleUser: (checked: boolean, userId: Id) => void
}) => ManageSkillsDialogViewModel

type CreateRemoveFromWorkspaceDialogViewModelFn = (
  onRemoved: (userPublicIds: Id[]) => void,
) => RemoveFromWorkspaceDialogViewModel

type CreateRestoreToWorkspaceDialogViewModelFn = (
  onRestored: (userPublicIds: Id[]) => void,
) => RestoreToWorkspaceDialogViewModel

export class UserManagementViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @observable
  accessor selectedUserIds = new Set<Id>()

  readonly columns = userManagementColumns

  readonly columnLayout

  readonly workspaceSelector

  readonly workspaceInvitationVm

  readonly manageSkillsVm

  readonly removeFromWorkspaceVm

  readonly restoreToWorkspaceVm

  constructor(
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _localStorage: Storage,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _getWorkspaceSelection: GetWorkspaceSelectionFn,
    private readonly _lastWorkspaceStorage: LastWorkspaceStorage,
    readonly userSearch: UserSearchViewModel,
    createWorkspaceInvitationDialogViewModel: CreateWorkspaceInvitationDialogViewModelFn,
    createManageSkillsDialogViewModel: CreateManageSkillsDialogViewModelFn,
    readonly manageUserCapacityVm: ManageUserCapacityDialogViewModel,
    readonly editUserRoleVm: EditUserRoleDialogViewModel,
    createRemoveFromWorkspaceDialogViewModel: CreateRemoveFromWorkspaceDialogViewModelFn,
    createRestoreToWorkspaceDialogViewModel: CreateRestoreToWorkspaceDialogViewModelFn,
  ) {
    this.columnLayout = new ColumnLayout(
      this._localStorage,
      'UserList Settings',
    )

    this.workspaceSelector = new WorkspaceSelectorViewModel(
      this._getCurrentMember,
      this._getCurrentUserWorkspaceMemberships,
      () => this.userSearch.workspaceFilter,
      () => this.selectedUserIds.clear(),
      () => this.userSearch.clearWorkspaceScopedFilters(),
      this._getWorkspaceSelection,
      this._lastWorkspaceStorage,
      () => Id.assert(this._getCurrentMember().user.publicId),
      this._localStorage,
    )

    this.workspaceInvitationVm = createWorkspaceInvitationDialogViewModel({
      selectedWorkspace: () => this.workspaceSelector.selectedWorkspace,
      selectedUserIds: () => this.selectedUserIds,
    })

    this.manageSkillsVm = createManageSkillsDialogViewModel({
      selectedWorkspace: () => this.workspaceSelector.selectedWorkspace,
      selectedUserIds: () => this.selectedUserIds,
      users: () => this.users,
      toggleUser: (checked, userId) => this.toggleUser(checked, userId),
    })

    this.removeFromWorkspaceVm = createRemoveFromWorkspaceDialogViewModel(
      (userIds) => this.removeUsers(userIds),
    )

    this.restoreToWorkspaceVm = createRestoreToWorkspaceDialogViewModel(
      (userIds) => this.removeUsers(userIds),
    )

    this._lifecycle.manage(this.userSearch)
    this._lifecycle.manage(this.workspaceSelector)
    this._lifecycle.manage(this.workspaceInvitationVm)
  }

  activate() {
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
    if (this.columnLayout.showSelection) {
      this.toggleShowSelection()
    }
  }

  isSelected(userId: Id) {
    return this.selectedUserIds.has(userId)
  }

  @computed
  get isPlatformAdmin() {
    return this._getCurrentMember().user.platformAdmin
  }

  @computed
  get isWorkspaceSelected() {
    return !!this.workspaceSelector.selectedWorkspace.id
  }

  @computed
  get canManageGlobalCapacity() {
    return (
      this.workspaceSelector.selectedWorkspace === Workspace.none &&
      this.isPlatformAdmin
    )
  }

  @computed
  get canManageSelectedUsersGlobalCapacity() {
    return this.canManageGlobalCapacity && this.selectedUserIds.size > 0
  }

  @computed
  get showSelectedUsersWorkspaceActions() {
    return this.isWorkspaceSelected && this.selectedUserIds.size > 0
  }

  @computed
  get isSearchFilterApplied() {
    const isSearching = this.userSearch.searchText.value !== ''
    const haveFiltersApplied = this.userSearch.filters.appliedFilterCount > 0

    return isSearching || haveFiltersApplied
  }

  @computed
  get shouldShowEmptyState() {
    return this.userSearch.scrollProvider.isDone && this.users.length === 0
  }

  @computed
  get canAccessUserManagement() {
    return this._getCurrentUserWorkspaceMemberships().some(
      (wms) => wms.member.permissions.adminUserList,
    )
  }

  @computed
  get selectedState() {
    if (this.selectedUserIds.size === 0) {
      return 'NoneSelected'
    } else if (this.users.length === this.selectedUserIds.size) {
      return 'AllSelected'
    } else {
      return 'SomeSelected'
    }
  }

  @computed
  get users() {
    return this.userSearch.scrollProvider.items
  }

  @action.bound
  toggleUser(checked: boolean, userId: Id) {
    if (checked) {
      this.selectedUserIds.add(userId)
    } else {
      this.selectedUserIds.delete(userId)
    }
  }

  @action.bound
  toggleAll() {
    switch (this.selectedState) {
      case 'AllSelected':
        this.selectedUserIds.clear()
        break
      case 'NoneSelected':
      case 'SomeSelected':
        this.selectedUserIds = new Set(this.users.map((user) => user.publicId))
        break
    }
  }

  @action.bound
  removeUsers(userIds: Id[]) {
    this.userSearch.removeUsers(userIds)
    this.selectedUserIds.clear()
  }

  @action.bound
  clearSelection() {
    this.selectedUserIds.clear()
  }

  @action.bound
  toggleShowSelection() {
    if (this.columnLayout.showSelection) {
      this.clearSelection()
    }
    this.columnLayout.toggleSelection()
  }

  @action.bound
  manageSelectedUsersCapacity() {
    this.manageUserCapacityVm.open(
      this.selectedUserIds,
      this.workspaceSelector.selectedWorkspace,
    )
  }

  @action.bound
  restoreSelectedUsers() {
    this.restoreToWorkspaceVm.open(
      this.selectedUserIds,
      this.workspaceSelector.selectedWorkspace,
    )
  }

  @action.bound
  editSelectedUsersRoles() {
    void this.editUserRoleVm.open(
      this.selectedUserIds,
      this.workspaceSelector.selectedWorkspace,
      this._lifecycle.signal,
    )
  }

  @action.bound
  removeSelectedUsers() {
    this.removeFromWorkspaceVm.open(
      this.selectedUserIds,
      this.workspaceSelector.selectedWorkspace,
    )
  }

  @action.bound
  inviteUser(user: SearchableUser) {
    this.workspaceInvitationVm.showWithUser(user)
  }

  @action.bound
  manageUserCapacity(user: SearchableUser) {
    void this.manageUserCapacityVm.showWithUser(
      user,
      this.workspaceSelector.selectedWorkspace,
    )
  }

  @action.bound
  restoreUser(user: SearchableUser) {
    this.restoreToWorkspaceVm.showWithUser(
      user,
      this.workspaceSelector.selectedWorkspace,
    )
  }

  @action.bound
  editUserRole(user: SearchableUser) {
    void this.editUserRoleVm.showWithUser(
      user,
      this.workspaceSelector.selectedWorkspace,
      this._lifecycle.signal,
    )
  }

  @action.bound
  manageUserSkills(user: SearchableUser) {
    this.manageSkillsVm.showWithUser(user)
  }

  @action.bound
  removeUser(user: SearchableUser) {
    this.removeFromWorkspaceVm.showWithUser(
      user,
      this.workspaceSelector.selectedWorkspace,
    )
  }

  /**
   * Clears any search filters.
   */
  @action.bound
  clearFilters() {
    this.userSearch.searchText.clear()
    this.userSearch.filters.clearExplicitFilters()
  }

  getUserDetailsLinkForUser(user: SearchableUser) {
    return links.userDetails(Id.assert(user.publicId))
  }
}
